// src/pages/ReportsPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart3,
  Download,
  Filter,
  TrendingUp,
  Users,
  Building2,
  MapPin,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Mail,
  Activity,
  Shield,
  Camera,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../theme/ThemeContext';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getGuardDisplayName = (guard) => {
  const name =
    guard?.name ||
    guard?.full_name ||
    guard?.guard_name ||
    [guard?.first_name, guard?.last_name].filter(Boolean).join(' ') ||
    null;

  if (!name) {
    return `Guard #${guard?.id ?? ''}`.trim();
  }

  return name;
};

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';

  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0].toUpperCase())
    .join('');
};

const getTrendIcon = (trend) => {
  if (trend === 'up') return <ArrowUpRight className="w-4 h-4" />;
  if (trend === 'down') return <ArrowDownRight className="w-4 h-4" />;
  return <Minus className="w-4 h-4" />;
};

const getTrendBadgeClass = (trend) => {
  if (trend === 'up') return 'text-emerald-600 bg-emerald-50';
  if (trend === 'down') return 'text-red-600 bg-red-50';
  return 'text-slate-600 bg-slate-50';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ReportsPage = () => {
  const { theme, setThemeName } = useTheme();
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState('this-month');
  const [reportType, setReportType] = useState('overview');
  const [showFilters, setShowFilters] = useState(false);

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [guardOptions, setGuardOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedGuardId, setSelectedGuardId] = useState('all');
  const [selectedClientId, setSelectedClientId] = useState('all');

  // -------------------------------------------------------------------------
  // Auth helpers
  // -------------------------------------------------------------------------

  const getAuthToken = () =>
    localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

  const getAuthRole = () =>
    localStorage.getItem('authRole') || sessionStorage.getItem('authRole');

  const logoutAndRedirect = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRole');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authRole');
    navigate('/');
  };

  // -------------------------------------------------------------------------
  // Fetch guards & clients
  // -------------------------------------------------------------------------

  useEffect(() => {
    const fetchGuardsAndClients = async () => {
      try {
        const token = getAuthToken();
        const role = getAuthRole();

        if (!token || role !== 'admin') {
          const msg = 'Session expired. Please log in again.';
          toast.error(msg);
          logoutAndRedirect();
          return;
        }

        // Guards
        const guardsRes = await fetch(
          'https://patrol-visit.patroll-management.nissiindia.com/api/guards',
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const guardsJson = await guardsRes.json().catch(() => []);

        if (guardsRes.status === 401) {
          const msg =
            guardsJson.message || 'Session expired. Please log in again.';
          toast.error(msg);
          logoutAndRedirect();
          return;
        }

        const guards = Array.isArray(guardsJson)
          ? guardsJson
          : guardsJson.data || guardsJson.guards || [];

        const mappedGuards = guards.map((g) => ({
          id: g.id,
          name:
            g.full_name ||
            g.name ||
            [g.first_name, g.last_name].filter(Boolean).join(' ') ||
            `Guard #${g.id}`,
        }));
        setGuardOptions(mappedGuards);

        // Clients
        const clientsRes = await fetch(
          'https://patrol-visit.patroll-management.nissiindia.com/api/clients',
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const clientsJson = await clientsRes.json().catch(() => []);

        if (clientsRes.status === 401) {
          const msg =
            clientsJson.message || 'Session expired. Please log in again.';
          toast.error(msg);
          logoutAndRedirect();
          return;
        }

        const clients = Array.isArray(clientsJson)
          ? clientsJson
          : clientsJson.data || clientsJson.clients || [];

        const mappedClients = clients.map((c) => ({
          id: c.id,
          name: c.full_name || c.name || `Client #${c.id}`,
        }));
        setClientOptions(mappedClients);
      } catch (err) {
        console.error('Error loading filter options:', err);
        toast.error('Could not load filters (guards/clients).');
      }
    };

    fetchGuardsAndClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Fetch report data
  // -------------------------------------------------------------------------

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError('');

        const token = getAuthToken();
        const role = getAuthRole();

        if (!token || role !== 'admin') {
          const msg = 'Session expired. Please log in again.';
          setError(msg);
          toast.error(msg);
          logoutAndRedirect();
          return;
        }

        const res = await fetch(
          `https://patrol-visit.patroll-management.nissiindia.com/api/reports?dateRange=${dateRange}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          if (res.status === 401) {
            const msg = json.message || 'Session expired. Please log in again.';
            setError(msg);
            toast.error(msg);
            logoutAndRedirect();
            return;
          }
          throw new Error(json.message || 'Failed to load reports');
        }

        setReportData(json);
      } catch (err) {
        console.error(err);
        const msg = err.message || 'Error loading reports';
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const guardPerformanceRaw = reportData?.guardPerformance || [];
  const clientActivityRaw = reportData?.clientActivity || [];
  const propertyInsights = reportData?.propertyInsights || [];
  const visitAnalytics =
    reportData?.visitAnalytics || {
      totalVisits: 0,
      completed: 0,
      pending: 0,
      missed: 0,
      averagePhotos: 0,
      averageVideos: 0,
      totalMedia: 0,
    };
  const monthlyTrends = reportData?.monthlyTrends || [];
  const runAllocation = reportData?.runAllocation || null;

  const guardPerformance = useMemo(() => {
    if (selectedGuardId === 'all') return guardPerformanceRaw;
    return guardPerformanceRaw.filter(
      (g) => String(g.id) === String(selectedGuardId)
    );
  }, [guardPerformanceRaw, selectedGuardId]);

  const clientActivity = useMemo(() => {
    if (selectedClientId === 'all') return clientActivityRaw;
    return clientActivityRaw.filter(
      (c) => String(c.id) === String(selectedClientId)
    );
  }, [clientActivityRaw, selectedClientId]);

  const overviewStats = useMemo(() => {
    if (!reportData?.overview) return [];

    const o = reportData.overview || {};

    const totalVisits = Number(o.totalVisits ?? 0);
    const activeGuards = Number(o.activeGuards ?? 0);
    const totalClients = Number(o.totalClients ?? 0);
    const totalProperties = Number(o.totalProperties ?? 0);
    const completionRate = Number(o.completionRate ?? 0);
    const avgResponseTimeMinutes = Number(o.avgResponseTimeMinutes ?? 0);

    const base = [
      {
        title: 'Total Visits',
        value: totalVisits.toLocaleString(),
        change: '+0%',
        trend: 'up',
        icon: MapPin,
        color: theme.primary[600],
        bgColor: theme.primary[50],
      },
      {
        title: 'Active Guards',
        value: activeGuards.toString(),
        change: '+0',
        trend: 'up',
        icon: Users,
        color: '#10b981',
        bgColor: '#d1fae5',
      },
      {
        title: 'Total Clients',
        value: totalClients.toString(),
        change: '+0%',
        trend: 'up',
        icon: Building2,
        color: '#f59e0b',
        bgColor: '#fef3c7',
      },
      {
        title: 'Properties',
        value: totalProperties.toString(),
        change: '+0%',
        trend: 'up',
        icon: Building2,
        color: '#8b5cf6',
        bgColor: '#ede9fe',
      },
      {
        title: 'Completion Rate',
        value: `${completionRate.toFixed(1)}%`,
        change: '+0%',
        trend: 'up',
        icon: CheckCircle,
        color: '#06b6d4',
        bgColor: '#cffafe',
      },
      {
        title: 'Avg Response Time',
        value: `${avgResponseTimeMinutes.toFixed(1)} min`,
        change: '+0 min',
        trend: 'up',
        icon: Clock,
        color: '#ec4899',
        bgColor: '#fce7f3',
      },
    ];

    if (runAllocation) {
      base.push({
        title: 'Allocated Runs',
        value: runAllocation.totalRuns.toString(),
        change: `${runAllocation.completedRuns} completed`,
        trend: 'up',
        icon: Activity,
        color: '#0ea5e9',
        bgColor: '#e0f2fe',
      });
    }

    return base;
  }, [reportData, theme, runAllocation]);

  // Recharts data for monthly trends
  const monthlyTrendData = useMemo(
    () =>
      (monthlyTrends || []).map((mt) => ({
        month: mt.month,
        visits: Number(mt.visits || 0),
        completed: Number(mt.completed || 0),
      })),
    [monthlyTrends]
  );

  // -------------------------------------------------------------------------
  // Export helpers
  // -------------------------------------------------------------------------

  const exportReport = (format) => {
    toast.success(`Exporting report as ${format}...`);
    const menu = document.getElementById('export-menu');
    if (menu) menu.classList.add('hidden');
  };

  const handleEmailReport = () => {
    toast('Sending report via email...');
    const menu = document.getElementById('export-menu');
    if (menu) menu.classList.add('hidden');
  };

  const handlePrintReport = () => {
    toast('Opening print dialog...');
    const menu = document.getElementById('export-menu');
    if (menu) menu.classList.add('hidden');
    window.print();
  };

  const toggleExportMenu = () => {
    const menu = document.getElementById('export-menu');
    if (menu) {
      menu.classList.toggle('hidden');
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
        }}
      />

      <div className="w-full max-w-6xl mx-auto space-y-4 sm:space-y-6 px-2 sm:px-4 lg:px-0 pb-20 sm:pb-8 overflow-x-hidden">
        {/* Header Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 sm:gap-4">
                <div
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
                  }}
                >
                  <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-800 truncate">
                    Reports &amp; Analytics
                  </h1>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1 truncate">
                    Comprehensive insights and performance metrics
                  </p>
                </div>
              </div>

              {/* Theme Selector */}
              <div className="flex items-center gap-2 justify-start sm:justify-end">
                <span className="text-[11px] sm:text-xs text-slate-500">
                  Theme
                </span>
                <div className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 p-1">
                  <button
                    type="button"
                    onClick={() => setThemeName('light')}
                    className="px-2 py-1 rounded-full text-[11px] sm:text-xs hover:bg-slate-100"
                  >
                    Light
                  </button>
                  <button
                    type="button"
                    onClick={() => setThemeName('purple')}
                    className="px-2 py-1 rounded-full text-[11px] sm:text-xs hover:bg-slate-100"
                  >
                    Purple
                  </button>
                  <button
                    type="button"
                    onClick={() => setThemeName('dark')}
                    className="px-2 py-1 rounded-full text-[11px] sm:text-xs hover:bg-slate-100"
                  >
                    Dark
                  </button>
                </div>
              </div>
            </div>

            {/* Controls row */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1 grid grid-cols-2 sm:flex sm:flex-1 gap-2 sm:gap-3">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all bg-white text-xs sm:text-sm"
                  style={{
                    '--tw-ring-color': `${theme.primary[500]}33`,
                  }}
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="this-week">This Week</option>
                  <option value="last-week">Last Week</option>
                  <option value="this-month">This Month</option>
                  <option value="last-month">Last Month</option>
                  <option value="this-year">This Year</option>
                  <option value="custom">Custom Range</option>
                </select>

                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all bg-white text-xs sm:text-sm"
                  style={{
                    '--tw-ring-color': `${theme.primary[500]}33`,
                  }}
                >
                  <option value="overview">Overview</option>
                  <option value="guards">Guards Performance</option>
                  <option value="clients">Client Activity</option>
                  <option value="properties">Property Insights</option>
                  <option value="visits">Visit Analytics</option>
                </select>
              </div>

              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex-1 sm:flex-initial p-2 sm:p-2.5 border-2 rounded-xl transition-all flex items-center justify-center gap-2 text-xs sm:text-sm font-medium ${
                    showFilters
                      ? 'bg-opacity-10'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                  style={
                    showFilters
                      ? {
                          borderColor: theme.primary[500],
                          backgroundColor: `${theme.primary[50]}`,
                          color: theme.primary[700],
                        }
                      : {}
                  }
                >
                  <Filter className="w-4 h-4" />
                  <span className="hidden sm:inline">Filters</span>
                </button>

                <div className="relative flex-1 sm:flex-initial">
                  <button
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-xl font-semibold hover:shadow-lg transition-all text-xs sm:text-sm"
                    style={{
                      background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
                    }}
                    onClick={toggleExportMenu}
                  >
                    <Download className="w-4 h-4" />
                    <span>Export</span>
                  </button>

                  <div
                    id="export-menu"
                    className="hidden absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-10"
                  >
                    <button
                      onClick={() => exportReport('PDF')}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Export as PDF
                    </button>
                    <button
                      onClick={() => exportReport('Excel')}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export as Excel
                    </button>
                    <button
                      onClick={() => exportReport('CSV')}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Export as CSV
                    </button>
                    <button
                      onClick={handleEmailReport}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      Email Report
                    </button>
                    <button
                      onClick={handlePrintReport}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Print
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Small loading / error for mobile */}
            <div className="sm:hidden flex items-center justify-between text-[11px] text-slate-500">
              {loading && <span>Loading...</span>}
              {error && !loading && (
                <span className="text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {error}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6 animate-fadeIn">
            <h3 className="text-sm font-bold text-slate-800 mb-4">
              Advanced Filters
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Guards */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                  Select Guards
                </label>
                <select
                  className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-xs sm:text-sm"
                  value={selectedGuardId}
                  onChange={(e) => setSelectedGuardId(e.target.value)}
                >
                  <option value="all">All Guards</option>
                  {guardOptions.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clients */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                  Select Clients
                </label>
                <select
                  className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-xs sm:text-sm"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                >
                  <option value="all">All Clients</option>
                  {clientOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Property Type (UI only) */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                  Property Type
                </label>
                <select className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-xs sm:text-sm">
                  <option>All Types</option>
                  <option>Commercial</option>
                  <option>Residential</option>
                  <option>Industrial</option>
                </select>
              </div>

              {/* Visit Status (UI only) */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                  Visit Status
                </label>
                <select className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-xs sm:text-sm">
                  <option>All Status</option>
                  <option>Completed</option>
                  <option>Pending</option>
                  <option>Missed</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {overviewStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-2 sm:mb-3">
                  <div
                    className="p-2 sm:p-2.5 rounded-lg group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: stat.bgColor }}
                  >
                    <Icon
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      style={{ color: stat.color }}
                    />
                  </div>
                </div>
                <h3 className="text-slate-600 text-[11px] sm:text-xs font-medium mb-1 line-clamp-1">
                  {stat.title}
                </h3>
                <div className="flex items-end justify-between">
                  <p className="text-lg sm:text-2xl font-bold text-slate-800">
                    {stat.value}
                  </p>
                  <span
                    className={`text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 rounded-full ${getTrendBadgeClass(
                      stat.trend
                    )}`}
                  >
                    {stat.change}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Guard Performance */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${theme.primary[50]}` }}
                >
                  <Shield
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    style={{ color: theme.primary[600] }}
                  />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-800">
                    Guard Performance
                  </h3>
                  <p className="text-xs text-slate-600">Top performers</p>
                </div>
              </div>
              <button
                className="text-xs sm:text-sm font-medium hover:underline"
                style={{ color: theme.primary[600] }}
              >
                View All
              </button>
            </div>

            <div className="space-y-3">
              {guardPerformance.map((guard) => {
                const displayName = getGuardDisplayName(guard);
                const initials = getInitials(displayName);

                return (
                  <div
                    key={guard.id ?? displayName}
                    className="p-3 sm:p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-semibold"
                          style={{
                            background: `linear-gradient(135deg, ${theme.primary[500]}, ${theme.secondary[500]})`,
                          }}
                        >
                          {initials}
                        </div>
                        <div>
                          <p className="font-semibold text-sm sm:text-base text-slate-800">
                            {displayName}
                          </p>
                          <p className="text-xs text-slate-600">
                            {guard.visits} visits
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 mb-1 justify-end">
                          <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-amber-500" />
                          <span className="text-xs sm:text-sm font-semibold text-slate-800">
                            {guard.rating}
                          </span>
                        </div>
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getTrendBadgeClass(
                            guard.trend
                          )}`}
                        >
                          {guard.change}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3 text-xs">
                      <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <span className="text-slate-600">Completion</span>
                        <span
                          className="font-semibold"
                          style={{ color: theme.primary[600] }}
                        >
                          {guard.completionRate}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <span className="text-slate-600">Avg Time</span>
                        <span
                          className="font-semibold"
                          style={{ color: theme.secondary[600] }}
                        >
                          {guard.avgTime}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!guardPerformance.length && !loading && (
                <p className="text-xs text-slate-500">
                  No guard data available for this period.
                </p>
              )}
            </div>
          </div>

          {/* Visit Analytics */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${theme.primary[50]}` }}
              >
                <Activity
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  style={{ color: theme.primary[600] }}
                />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-800">
                  Visit Analytics
                </h3>
                <p className="text-xs text-slate-600">Current period summary</p>
              </div>
            </div>

            <div className="space-y-3">
              {/* Completed */}
              <div
                className="p-3 sm:p-4 rounded-xl"
                style={{
                  background: `linear-gradient(135deg, ${theme.primary[50]}, ${theme.secondary[50]})`,
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm text-slate-700">
                    Completed Visits
                  </span>
                  <CheckCircle
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    style={{ color: theme.primary[600] }}
                  />
                </div>
                <p
                  className="text-2xl sm:text-3xl font-bold"
                  style={{ color: theme.primary[700] }}
                >
                  {visitAnalytics.completed}
                </p>
                <div
                  className="mt-2 h-2 rounded-full overflow-hidden"
                  style={{ backgroundColor: theme.primary[100] }}
                >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${
                        visitAnalytics.totalVisits
                          ? (visitAnalytics.completed /
                              visitAnalytics.totalVisits) *
                            100
                          : 0
                      }%`,
                      background: `linear-gradient(90deg, ${theme.primary[500]}, ${theme.secondary[500]})`,
                    }}
                  ></div>
                </div>
                <p
                  className="text-xs mt-1"
                  style={{ color: theme.primary[700] }}
                >
                  {visitAnalytics.totalVisits
                    ? (
                        (visitAnalytics.completed /
                          visitAnalytics.totalVisits) *
                        100
                      ).toFixed(1)
                    : '0.0'}
                  % of total
                </p>
              </div>

              {/* Pending */}
              <div className="p-3 sm:p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm text-slate-700">
                    Pending
                  </span>
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-amber-700">
                  {visitAnalytics.pending}
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Awaiting completion
                </p>
              </div>

              {/* Missed */}
              <div className="p-3 sm:p-4 bg-gradient-to-br from-red-50 to-pink-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm text-slate-700">
                    Missed
                  </span>
                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-red-700">
                  {visitAnalytics.missed}
                </p>
                <p className="text-xs text-red-700 mt-1">Require attention</p>
              </div>

              {/* Media captured */}
              <div
                className="p-3 sm:p-4 rounded-xl"
                style={{
                  background: `linear-gradient(135deg, ${theme.secondary[50]}, ${theme.primary[50]})`,
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm text-slate-700">
                    Media Captured
                  </span>
                  <Camera
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    style={{ color: theme.secondary[600] }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div>
                    <p
                      className="text-xl sm:text-2xl font-bold"
                      style={{ color: theme.primary[700] }}
                    >
                      {visitAnalytics.averagePhotos.toFixed
                        ? visitAnalytics.averagePhotos.toFixed(1)
                        : visitAnalytics.averagePhotos}
                    </p>
                    <p className="text-xs" style={{ color: theme.primary[600] }}>
                      Avg Photos
                    </p>
                  </div>
                  <div>
                    <p
                      className="text-xl sm:text-2xl font-bold"
                      style={{ color: theme.secondary[700] }}
                    >
                      {visitAnalytics.averageVideos.toFixed
                        ? visitAnalytics.averageVideos.toFixed(1)
                        : visitAnalytics.averageVideos}
                    </p>
                    <p
                      className="text-xs"
                      style={{ color: theme.secondary[600] }}
                    >
                      Avg Videos
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-600 mt-2">
                  Total media this period: {visitAnalytics.totalMedia}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Property Insights & Client Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Property Insights */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-800">
                  Property Distribution
                </h3>
                <p className="text-xs text-slate-600">By property type</p>
              </div>
            </div>

            <div className="space-y-3">
              {propertyInsights.map((property, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs sm:text-sm font-semibold text-slate-800">
                        {property.type}
                      </span>
                      {getTrendIcon(property.trend)}
                    </div>
                    <div className="text-right">
                      <span className="text-xs sm:text-sm font-bold text-slate-800">
                        {property.count}
                      </span>
                      <span className="text-xs text-slate-600">
                        {' '}
                        properties
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${property.percentage}%`,
                          background: `linear-gradient(90deg, ${theme.primary[500]}, ${theme.secondary[500]})`,
                        }}
                      ></div>
                    </div>
                    <span className="text-xs font-semibold text-slate-600 w-12 text-right">
                      {property.percentage}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-600">
                    {property.visits} total visits
                  </p>
                </div>
              ))}
              {!propertyInsights.length && !loading && (
                <p className="text-xs text-slate-500">
                  No property data available for this period.
                </p>
              )}
            </div>
          </div>

          {/* Client Activity */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-800">
                    Top Clients
                  </h3>
                  <p className="text-xs text-slate-600">
                    Most active this period
                  </p>
                </div>
              </div>
              <button
                className="text-xs sm:text-sm font-medium hover:underline"
                style={{ color: theme.primary[600] }}
              >
                View All
              </button>
            </div>

            <div className="space-y-3">
              {clientActivity.map((client) => (
                <div
                  key={client.id}
                  className="p-3 sm:p-4 border-2 border-slate-100 rounded-xl hover:border-slate-200 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm sm:text-base text-slate-800 truncate">
                        {client.name}
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        Last visit: {client.lastVisit}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-amber-500" />
                      <span className="text-xs sm:text-sm font-semibold">
                        {client.satisfaction}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3 h-3 text-slate-400" />
                      <span className="text-slate-600">
                        {client.properties} properties
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400" />
                      <span className="text-slate-600">
                        {client.visits} visits
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {!clientActivity.length && !loading && (
                <p className="text-xs text-slate-500">
                  No client data available for this period.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Monthly Trends (Recharts) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <div
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${theme.primary[50]}` }}
            >
              <TrendingUp
                className="w-4 h-4 sm:w-5 sm:h-5"
                style={{ color: theme.primary[600] }}
              />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-slate-800">
                Monthly Trends
              </h3>
              <p className="text-xs text-slate-600">
                Visit completion over time
              </p>
            </div>
          </div>

          {monthlyTrendData.length > 0 ? (
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyTrendData} barCategoryGap={24}>
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }}
                    contentStyle={{
                      borderRadius: '0.75rem',
                      border: '1px solid #e2e8f0',
                      fontSize: '11px',
                    }}
                  />
                  <Bar
                    dataKey="visits"
                    radius={[6, 6, 0, 0]}
                    fill={theme.primary[500]}
                  />
                  <Bar
                    dataKey="completed"
                    radius={[6, 6, 0, 0]}
                    fill={theme.secondary[500]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
              <AlertCircle className="h-4 w-4" />
              <span>No trend data available.</span>
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 mt-6 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: theme.primary[500] }}
              ></div>
              <span className="text-xs sm:text-sm text-slate-700">
                Total Visits
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: theme.secondary[500] }}
              ></div>
              <span className="text-xs sm:text-sm text-slate-700">
                Completed
              </span>
            </div>
          </div>
        </div>

        {/* Small CSS helper for fadeIn */}
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out;
          }
        `}</style>
      </div>
    </>
  );
};

export default ReportsPage;
